import {CheckItemsManager} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/check-items-manager';
import {Step} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/step';
import {Observable} from 'rxjs';
import {TestPassState, TestResponse, TestResponseCommands} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {Role} from '@app/modules/shared/data/constants';
import Bowser from "bowser";


export class ScreenCheckManager extends CheckItemsManager {
  icon = 'screen-g';
  name = 'Screen';

  constructor(userRole: Role) {
    super(userRole);
    this.steps.push(new BrowserCheckStep(), new ScreenCheckStep(userRole), new ZoomCheckStep(userRole));
  }
}


export class BrowserCheckStep extends Step {
  name = 'Browser';
  title = 'Checking browser...';

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      const browserName = this.getBrowserName();
      const browserVersion = this.detectBrowserVersion();
      if (browserName !== 'Chrome') {
        // eslint-disable-next-line max-len
        this.testResult.errorMassage = 'The virtual classroom is currently only supported on Google Chrome. You will not be able to log in without Chrome. If you seek additional help, reach out to tech support using the chat bubble at the bottom or contact the office.';
        this.testResult.status = TestPassState.FAILED;
        this.testResult.text = 'Detected ' + browserName + ' ' + browserVersion;
        this.testStatus = 'NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = browserName + browserVersion;
        this.testStatus = browserVersion;
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
      subscriber.complete();
    });
  }

  getBrowserName(): string {
    const browser = Bowser.getParser(window.navigator.userAgent).getBrowserName();
    return browser;
  }

  detectBrowserVersion() {
    const browserVersion = Bowser.getParser(window.navigator.userAgent).getBrowserVersion();
    return browserVersion.split('.')[0];
  }
}

export class ScreenCheckStep extends Step {
  name = 'Screen resolution';
  title = 'Checking screen resolution...';
  userRole: Role;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MIN_SCREEN_WIDTH_TEACHER = 1920;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MIN_SCREEN_HEIGHT_TEACHER = 1080;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MIN_SCREEN_WIDTH = 1366;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MIN_SCREEN_HEIGHT = 768;

  constructor(userRole: Role) {
    super();
    this.userRole = userRole;
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      // setTimeout(() => {
        const screenWidth = this.getScreenWidth();
        const screenHeight = this.getScreenHeight();
        const minimumScreenWidth = this.userRole !== Role.STUDENT ? this.MIN_SCREEN_WIDTH_TEACHER : this.MIN_SCREEN_WIDTH;
        const minimumScreenHeight = this.userRole !== Role.STUDENT ? this.MIN_SCREEN_HEIGHT_TEACHER : this.MIN_SCREEN_HEIGHT;
        if (screenWidth >= minimumScreenWidth || screenHeight >= minimumScreenHeight) {
          this.testResult.status = TestPassState.PASSED;
          this.testResult.text = screenWidth + 'x' + screenHeight;
          this.testStatus = this.testResult.text;
          subscriber.next(new TestResponse(TestResponseCommands.NEXT));
        } else {
          // eslint-disable-next-line max-len
          this.testResult.errorMassage = 'Your screen resolution is too low for the virtual classroom. To fix this, increase your screen resolution, then click Retry. If you\'re already at maximum resolution, click Skip. You may need to use a different computer for future class sessions. If you seek additional help, reach out to tech support using the chat bubble at the bottom or contact the office.';
          this.testResult.status = TestPassState.FAILED;
          this.testResult.text = screenWidth + 'x' + screenHeight;
          this.testStatus = this.testResult.text;
          subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
        }
      // }, 1000);
    });
  }

  getScreenWidth(): number {
    return screen.width;
  }

  getScreenHeight(): number {
    return screen.height;
  }
}

export class ZoomCheckStep extends Step {
  name = 'Zooming';
  title = 'Checking screen zooming...';
  userRole: Role;

  constructor(userRole: Role) {
    super();
    this.userRole = userRole;
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      // setTimeout(() => {
        const ratio = window.devicePixelRatio;
        if (ratio === 1) {
          this.testResult.status = TestPassState.PASSED;
          this.testResult.text = 'No zoom';
          this.testStatus = 'OK';
          subscriber.next(new TestResponse(TestResponseCommands.NEXT));
        } else {
          if (this.userRole === Role.STUDENT) {
            this.testResult.status = TestPassState.PASSED;
            this.testResult.text = 'Zoom acceptable';
            this.testStatus = 'OK';
            subscriber.next(new TestResponse(TestResponseCommands.NEXT));
          } else {
            if (ratio < 1) {
              this.testResult.text = 'Zoom: Your screen is zoomed OUT';
            } else if (ratio > 1) {
              this.testResult.text = 'Zoom: Your screen is zoomed IN';
            }
            this.testResult.errorMassage = 'Your screen is zoomed IN/OUT. Please press CTRL+0 for returning to normal zooming';
            this.testResult.status = TestPassState.FAILED;
            this.testStatus = 'NO';
            subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
          }
        }
      // },1000);
    });
  }
}
